import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  FaChartPie,
  FaProjectDiagram,
  FaCubes,
  FaHeartbeat,
  FaExclamationTriangle,
  FaShieldAlt,
  FaSyncAlt
} from 'react-icons/fa';
import AnalyticsFilterBar from './components/AnalyticsFilterBar';
import { useIapAnalyticsData } from './hooks/useIapAnalyticsData';
import { getAnalyticsFlowsForSite } from '../../services/analytics.service';
import { trackEvent } from '../../services/applicationInsights';

import OverviewTab from './components/tabs/OverviewTab';
import FlowAdoptionTab from './components/tabs/FlowAdoptionTab';
import ModuleUsageTab from './components/tabs/ModuleUsageTab';
import PipelineHealthTab from './components/tabs/PipelineHealthTab';

import './IapUsageAnalyticsHub.css';

const TABS = [
  { id: 'overview', label: 'Overview', icon: <FaChartPie />, requiresEntitlement: true },
  { id: 'flowAdoption', label: 'Flow Adoption', icon: <FaProjectDiagram />, requiresEntitlement: true },
  { id: 'moduleUsage', label: 'Module Usage', icon: <FaCubes />, requiresEntitlement: true },
  { id: 'pipelineHealth', label: 'Pipeline Health', icon: <FaHeartbeat />, requiresEntitlement: false }
];

/**
 * Unified IAP Usage Analytics Hub component.
 * Integrates filter controls, data orchestration hook, flow resolution, and 4 specialized tab views.
 */
export default function IapUsageAnalyticsHub({
  orgId,
  roles = [],
  hasAdvancedAnalyticsFeature = true
}) {
  const [activeTab, setActiveTab] = useState(() => (hasAdvancedAnalyticsFeature ? 'overview' : 'pipelineHealth'));
  const [filters, setFilters] = useState({});
  const [flows, setFlows] = useState([]);
  const [flowsLoading, setFlowsLoading] = useState(false);

  // Hook query execution
  const {
    overview,
    flowAdoption,
    moduleUsage,
    pipelineHealth,
    hasAdvancedAnalytics,
    entitlementDenied,
    loading,
    error,
    refetch
  } = useIapAnalyticsData(filters, {
    enabled: Boolean(filters.siteCollectionId && filters.fromUtc && filters.toUtc)
  });

  // Effective entitlement: combination of prop gate and service API entitlement response
  const isAdvancedEntitled = hasAdvancedAnalyticsFeature && hasAdvancedAnalytics && !entitlementDenied;

  // Load flow registry for human-readable flow names
  useEffect(() => {
    let isMounted = true;
    const fetchFlows = async () => {
      setFlows([]);
      if (!filters.organizationId || !filters.siteCollectionId) {
        return;
      }
      setFlowsLoading(true);
      try {
        const result = await getAnalyticsFlowsForSite({
          organizationId: filters.organizationId,
          siteCollectionId: filters.siteCollectionId
        });
        if (isMounted) {
          setFlows(Array.isArray(result) ? result : []);
        }
      } catch (err) {
        if (isMounted) {
          setFlows([]);
        }
      } finally {
        if (isMounted) {
          setFlowsLoading(false);
        }
      }
    };

    fetchFlows();
    return () => {
      isMounted = false;
    };
  }, [filters.organizationId, filters.siteCollectionId]);

  // Non-blocking telemetry emission
  const lastTelemetryViewedRef = useRef(null);
  useEffect(() => {
    if (filters.siteCollectionId) {
      const viewKey = `${filters.siteCollectionId}:${activeTab}`;
      if (lastTelemetryViewedRef.current !== viewKey) {
        lastTelemetryViewedRef.current = viewKey;
        trackEvent('iap_usage_analytics_viewed', {
          organizationId: filters.organizationId || orgId,
          siteCollectionId: filters.siteCollectionId,
          tab: activeTab,
          section: 'analytics-iap',
          skipTelemetryTrack: true
        });
      }
    }
  }, [filters.organizationId, filters.siteCollectionId, activeTab, orgId]);

  const [refreshKey, setRefreshKey] = useState(0);

  const handleFilterChange = useCallback((newFilters) => {
    setFilters(newFilters);
  }, []);

  const handleRefresh = useCallback(() => {
    setRefreshKey((prev) => prev + 1);
    refetch();
  }, [refetch]);

  const effectiveHubFilters = useMemo(() => ({
    ...filters,
    refreshKey
  }), [filters, refreshKey]);

  const currentTabMeta = TABS.find((t) => t.id === activeTab) || TABS[0];
  const isCurrentTabLocked = currentTabMeta.requiresEntitlement && !isAdvancedEntitled;

  return (
    <div className="iap-analytics-hub" data-testid="iap-usage-analytics-hub">
      {/* Header */}
      <header className="hub-header">
        <div className="hub-header-title-wrap">
          <h1>IAP Analytics</h1>
          <p>Unified operational, adoption, and ingestion metrics across digital workflows and runtime modules</p>
        </div>
        <div className={`hub-header-badge ${isAdvancedEntitled ? 'badge-advanced' : 'badge-baseline'}`}>
          <FaShieldAlt /> {isAdvancedEntitled ? 'Advanced Analytics Tier' : 'Baseline Licensing'}
        </div>
      </header>

      {/* Filter Bar */}
      <div className="hub-filter-container">
        <AnalyticsFilterBar
          orgId={orgId}
          roles={roles}
          onFilterChange={handleFilterChange}
          onRefresh={handleRefresh}
          isLoading={loading || flowsLoading}
          defaultPreset="7d"
        />
      </div>

      {/* Error Notice */}
      {error && (
        <div className="hub-notice hub-error-notice" role="alert" data-testid="hub-error-notice">
          <div className="hub-notice-content">
            <FaExclamationTriangle className="hub-notice-icon" />
            <span>{error}</span>
          </div>
          <button type="button" className="hub-retry-btn" onClick={handleRefresh}>
            <FaSyncAlt /> Retry
          </button>
        </div>
      )}

      {/* Tabs Navigation Bar */}
      <nav className="hub-tabs-nav-bar" role="tablist" aria-label="Analytics Tabs">
        {TABS.map((tab) => {
          const isActive = activeTab === tab.id;
          const isLocked = tab.requiresEntitlement && !isAdvancedEntitled;
          return (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={isActive}
              className={`hub-tab-btn ${isActive ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
              data-testid={`tab-btn-${tab.id}`}
            >
              <span className="hub-tab-icon">{tab.icon}</span>
              <span>{tab.label}</span>
              {isLocked && (
                <span className="tab-badge badge-locked">Locked</span>
              )}
            </button>
          );
        })}
      </nav>

      {/* Tab Panes */}
      <main className="hub-tab-content-area" role="tabpanel" aria-label={currentTabMeta.label}>
        {isCurrentTabLocked ? (
          <div className="tab-content-pane locked-pane" data-testid="hub-tab-locked-notice">
            <div className="entitlement-upgrade-card">
              <h3 className="upgrade-title">{currentTabMeta.label} Requires Advanced Analytics</h3>
              <p className="upgrade-description">
                In-depth user activity, workflow adoption, and client runtime metrics require the <strong>Advanced Analytics</strong> tier.
                Your organization is currently operating under baseline licensing.
              </p>
              <div className="upgrade-actions">
                <button
                  type="button"
                  className="btn btn-secondary-outline"
                  onClick={() => setActiveTab('pipelineHealth')}
                  data-testid="locked-switch-to-pipeline"
                >
                  View Pipeline Health
                </button>
              </div>
            </div>
          </div>
        ) : (
          <>
            {activeTab === 'overview' && (
              <OverviewTab
                overview={overview}
                featureAdoption={flowAdoption?.data}
                flows={flows}
                loading={loading}
                filters={effectiveHubFilters}
              />
            )}

            {activeTab === 'flowAdoption' && (
              <FlowAdoptionTab
                flowAdoption={flowAdoption}
                flows={flows}
                loading={loading}
                filters={effectiveHubFilters}
              />
            )}

            {activeTab === 'moduleUsage' && (
              <ModuleUsageTab
                moduleUsage={moduleUsage}
                loading={loading}
                entitlementDenied={!isAdvancedEntitled}
                onNavigateToPipeline={() => setActiveTab('pipelineHealth')}
              />
            )}

            {activeTab === 'pipelineHealth' && (
              <PipelineHealthTab
                pipelineHealth={pipelineHealth}
                loading={loading}
              />
            )}
          </>
        )}
      </main>
    </div>
  );
}

IapUsageAnalyticsHub.propTypes = {
  orgId: PropTypes.string,
  roles: PropTypes.arrayOf(PropTypes.string),
  hasAdvancedAnalyticsFeature: PropTypes.bool
};
